import { Component, type ErrorInfo, type ReactNode } from 'react'

import { Button } from '@/components/ui/Button'
import { ErrorState } from '@/components/ui/ErrorState'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

/** Keeps a render failure in one region from blanking the whole app. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unhandled render error', error, info.componentStack)
  }

  render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <ErrorState
        error={error}
        fallbackTitle="This view failed to render"
        onRetry={() => this.setState({ error: null })}
        actions={
          <Button variant="secondary" onClick={() => window.location.reload()}>
            Reload the page
          </Button>
        }
      />
    )
  }
}
